import { ArrowRight, Users, Ticket, ShoppingBag, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const icons = [Users, Ticket, ShoppingBag, UserPlus];

const KommunikationAutomationSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject, localizePath } = useLanguage();
  const items = tObject<Array<{ from: string; to: string }>>('aiAgentPlattform.automation.items');

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/50 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('aiAgentPlattform.automation.badge')}</Badge>
            
            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t('aiAgentPlattform.automation.titleLine1')}</span>
              <br />
              <span className="text-foreground">{t('aiAgentPlattform.automation.titleLine2')}</span>
            </h2>
          </div>

          <p className="text-xl font-medium text-primary text-center mb-8">
            {t('aiAgentPlattform.automation.intro')}
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {items.map((item, index) => {
              const Icon = icons[index];
              return (
                <div
                  key={index}
                  className={cn(
                    "bg-white rounded-xl p-6 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,196,180,0.14)] transition-all",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  )}
                  style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms", transitionDuration: "500ms" }}
                >
                  <Icon className="w-8 h-8 text-chathero-mint mx-auto mb-4" />
                  <div className="flex items-center justify-center gap-2 text-primary font-medium">
                    <span>{item.from}</span>
                    <ArrowRight className="w-4 h-4 text-chathero-orange" />
                    <span>{item.to}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div 
            className={cn(
              "bg-chathero-mint/10 rounded-2xl p-6 text-center mb-8 border border-chathero-mint/20 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
          >
            <p className="text-xl font-semibold text-primary">
              {t('aiAgentPlattform.automation.highlight')}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            <Link 
              to={localizePath("/produkte/ai-automation-workflows")}
              className="inline-flex items-center gap-2 text-chathero-mint font-semibold hover:gap-3 transition-all"
            >
              {t('aiAgentPlattform.automation.linkWorkflows')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to={localizePath("/integrationen")}
              className="inline-flex items-center gap-2 font-semibold text-chathero-orange hover:text-chathero-orange/80 hover:gap-3 transition-all"
            >
              {t('aiAgentPlattform.automation.linkIntegrations')}
              <ArrowRight className="w-4 h-4 text-chathero-orange" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KommunikationAutomationSection;
